import { Navigate, Outlet } from 'react-router-dom'
import { useFlag } from '@/modules/feature-flags/hooks/useFeatureFlags'
import { Button } from '@/components/ui/button'
import { Layout } from '@/components/layout/Layout'
import { QueryError } from '@/components/ui/query-error'
import { Spinner } from '@/components/ui/spinner'

export const FeatureFlagRoute = ({ flag }: { flag: string }) => {
  const { enabled, isPending, isUnresolvable, error, isRetrying, retry } = useFlag(flag)

  // the guarded pages each bring their own Layout, so these two branches have to supply
  // one themselves — otherwise they strand the user on a bare page with no header to
  // navigate away from.
  if (isPending) {
    return (
      <Layout>
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      </Layout>
    )
  }

  // unreachable flags are not a disabled feature. redirecting here would silently swallow
  // the page the user asked for — and on a refresh their URL with it — over what is usually
  // a transient blip or a moment offline, so show what happened and offer a retry.
  if (isUnresolvable) {
    return (
      <Layout>
        <div className="flex flex-col items-center gap-4">
          <QueryError
            error={error}
            fallback="Feature flags are unavailable — you appear to be offline."
            testId="feature-flag-route-error"
          />
          <Button variant="outline" onClick={retry} disabled={isRetrying}>
            {isRetrying ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      </Layout>
    )
  }

  if (!enabled) {
    return <Navigate to="/" replace />
  }

  return <Outlet />
}
